import { useFormik } from "formik";
import React, { useState } from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { getFavouriteCategoryImage, updateFavourite } from "../api";

const useAddFavouriteCategory = ({
  refetch,
  favouriteCategoryState,
  handleModal,
  setFvouriteCategoryState,
}) => {
  const [imageUploadState, setImageUploadState] = useState({
    imageLoading: false,
    imageAliasLoading: false,
  });
  const validationSchema = Yup.object({
    name: Yup.string()
      .required("Name is required")
      .test(
        "no-leading-space",
        "Name should not start with a space",
        (value) => !value || !/^\s/.test(value)
      )
      .matches(/^[A-Za-z\s]+$/, "Only letters and spaces are allowed")
      .max(25, "Name must be at most 25 characters"),
    image: Yup.string().required("Please upload an image"),
    alias_image: Yup.string().required("Please upload an image"),
  });
  const formik = useFormik({
    initialValues: {
      name: favouriteCategoryState?.itemData?.name ?? "",
      image: favouriteCategoryState?.itemData?.image ?? "",
      alias_image: favouriteCategoryState?.itemData?.alias_image ?? "",
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      let data = {
        name: values?.name,
        image: values?.image,
        alias_image: values?.alias_image,
        ...(favouriteCategoryState?.isEdit && {
          id: favouriteCategoryState?.itemId,
        }),
      };
      updateFavourite(data).then((response) => {
        if (response?.data?.success) {
          toast.success(
            response?.data?.data ??
              "Favourite category icon Updated successfully"
          );
          refetch();
          handleModal();
          setSubmitting(false);
        } else if (response?.data?.status_code === 422) {
          let errors = response?.errors;
          let errorFields = Object.keys(errors);
          errorFields?.forEach((field) => {
            formik.setFieldError(field, errors[field]);
          });
          setSubmitting(false);
        } else if (response?.success === false) {
          let errors = response?.message;
          let errorFields = Object.keys(errors);
          errorFields?.forEach((field) => {
            formik.setFieldError(field, errors[field]);
          });
          setSubmitting(false);
        } else {
          toast.error(response?.data?.message ?? "Something went wrong");
          setSubmitting(false);
        }
      });
    },
  });

  const handleImageChange = async (event, fieldName) => {
    const selectedLogo = event?.target?.files[0];
    if (selectedLogo) {
      if (
        !["image/jpeg", "image/png", "image/jpg"].includes(selectedLogo.type)
      ) {
        formik.setFieldError(
          `${fieldName === "image" ? "image" : "alias_image"}`,
          "Only image files are allowed"
        );
        return;
      }
      const maxSize = 3 * 1024 * 1024;
      if (selectedLogo.size > maxSize) {
        formik.setFieldError(
          `${fieldName === "image" ? "image" : "alias_image"}`,
          "Image size should be less than 3MB"
        );
        return;
      }
      // File type validation
      setImageUploadState((prev) => ({
        ...prev,
        ...(fieldName === "image"
          ? { imageLoading: true }
          : { imageAliasLoading: true }),
      }));

      const formData = new FormData();
      formData.append("image", selectedLogo);
      await getFavouriteCategoryImage(formData).then((res) => {
        if (res?.data?.status_code === 200) {
          formik.setFieldValue(fieldName, res?.data?.data?.image_path);
          if (fieldName === "image") {
            setImageUploadState((prev) => ({
              ...prev,
              imageLoading: false,
            }));
            setFvouriteCategoryState((prev) => ({
              ...prev,
              imageFile: URL.createObjectURL(selectedLogo) ?? "",
            }));
          } else {
            setImageUploadState((prev) => ({
              ...prev,
              imageAliasLoading: false,
            }));
            setFvouriteCategoryState((prev) => ({
              ...prev,
              imageAliasFile: URL.createObjectURL(selectedLogo) ?? "",
            }));
          }
        } else if (res?.status_code === 422 && res?.errors) {
          Object.keys(res?.errors).forEach((field) => {
            formik.setFieldError(`${fieldName}`, res?.errors[field]);
          });
        } else {
          toast.error(`Unable to add image, Please try again.`);
        }
      });
    }
  };
  const getFieldError = (fieldName) => {
    if (formik.touched[fieldName] && formik.errors[fieldName]) {
      return formik.errors[fieldName];
    }
    return "";
  };

  return { formik, imageUploadState, getFieldError, handleImageChange };
};

export default useAddFavouriteCategory;
